/**
 * Subscriptions repository — the ONLY place the frontend touches
 * `subscriptions`, `subscription_plans`, `subscription_pricing_tiers` and
 * `pricing_settings` data.
 *
 * ── Migration state (N-4 / slice a13) ─────────────────────────────────────────
 *   Step 1 (done)  — consolidated every scattered `supabase.from(...)` call here.
 *   Step 2 (done)  — guarded NestJS endpoints (`apps/api/src/subscriptions/*`,
 *                    Prisma-backed; guards mirror today's RLS posture).
 *   Step 3 (HERE)  — every method is Nest-first with an armed Supabase fallback:
 *                    responses zod-parse against `@dallali/shared` (garbage →
 *                    throw → fallback), fallbacks report via
 *                    `src/lib/nestFallback.ts`. RLS untouched — a bad Nest
 *                    deploy degrades to today's behavior automatically.
 *
 * ── Flip notes ────────────────────────────────────────────────────────────────
 *   `updatePlanSelection` keeps its (id, companyId, patch) signature, but the
 *   Nest path sends NO id — the server derives the row from the JWT company
 *   (one sub per company), so a stale/foreign id can never cross tenants. The
 *   Supabase fallback keeps the id+company double-filter.
 *   `getPricingSettingValue` is Nest-backed ONLY for the allowlisted
 *   `service_catalog` key (the single non-SA config read the server exposes);
 *   any other key goes straight to the Supabase path.
 *   Super-admin dashboard counts collapsed into ONE `adminStatsForSuperAdmin()`
 *   aggregate (GET /subscriptions/admin/stats); its Supabase fallback composes
 *   the same 5 legacy queries.
 *
 * ── Quota enforcement is intentionally ABSENT (do not "restore" it) ────────────
 *   The N-4 spec asked to "enforce plan limits server-side". That line is STALE.
 *   The self_admin subscription model was retired March 2026; the
 *   `check_unit_limit` / `check_user_limit` triggers were deliberately DROPPED in
 *   PR #2597 (migration 20260617150000) because they fired spuriously and
 *   surfaced as HTTP 500/400 (Sentry #2592 / #2593). The FE limit hooks
 *   (`useSubscriptionLimits`) are permanent no-ops returning Infinity. Re-adding
 *   server-side enforcement would reintroduce a fixed production bug — it
 *   requires a product decision to un-retire the model, not a migration move.
 *
 * ── Auth split ────────────────────────────────────────────────────────────────
 *   Company-scoped reads/writes resolve the company from the JWT on the Nest
 *   path (fallback filters `company_id`). Plan-catalog reads
 *   (`listActivePlans`, `listPricingTiers`) are config any authenticated
 *   context may read. Cross-tenant reads (`*ForSuperAdmin`) intentionally span
 *   ALL companies — SuperAdminGuard on the Nest side, super_admin RLS on the
 *   fallback; they MUST NOT be given a per-company filter. Pricing config
 *   writes (`upsertPricingSetting`, `*PricingTier`) are super_admin-only.
 *
 *   (The deprecated self_admin subscribe tree — the last `from("subscriptions")`
 *   call site outside this repository — was deleted in the a13 erasure commit;
 *   this file now holds every FE touch of the four subscription tables.)
 */
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { serializeError } from "@/lib/sentry";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { SubscriptionStatus } from "@/types/subscription";
import {
  pricingSettingSchema,
  serviceCatalogValueSchema,
  subscriptionAdminStatsSchema,
  subscriptionPlanSchema,
  subscriptionPricingTierSchema,
  subscriptionSchema,
  subscriptionWithCompanySchema,
} from "@dallali/shared";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest, NestRequestError } from "@/services/nestRequest";
import {
  reportNestFallback,
  reportNestFallbackOrRethrowUnknownCommit,
} from "@/lib/nestFallback";

export type SubscriptionIdRow = Pick<Tables<"subscriptions">, "id">;
export type SubscriptionRow = Tables<"subscriptions">;
export type SubscriptionPlanRow = Tables<"subscription_plans">;
export type PricingTierRow = Tables<"subscription_pricing_tiers">;
export type PricingSettingRow = Tables<"pricing_settings">;

/** A subscription row with its embedded company, for the super-admin subscribers list. */
export type SubscriptionWithCompanyRow = SubscriptionRow & {
  company: Pick<Tables<"companies">, "id" | "name" | "name_en" | "email" | "phone"> | null;
};

/** The `GET /subscriptions/admin/stats` aggregate (5 legacy queries in one). */
export interface SubscriptionAdminStats {
  total_excluding_canceled: number;
  active: number;
  trial: number;
  new_this_month_excluding_canceled: number;
  active_balance_sum: number;
}

// Authenticated Nest request bound to the "subscriptions" slice — shared
// transport (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
const nestRequest = makeNestRequest("subscriptions");

// ─── Narrowing helpers (strict:false → z.infer degrades keys to optional; the
//     parse already guaranteed the shape, rebuild to the required-key rows —
//     the toProfileLookupRow precedent) ────────────────────────────────────────

function toSubscriptionRow(s: z.infer<typeof subscriptionSchema>): SubscriptionRow {
  return {
    id: s.id!,
    company_id: s.company_id!,
    plan_id: s.plan_id ?? null,
    status: s.status!,
    billing_cycle: s.billing_cycle!,
    unit_limit: s.unit_limit!,
    user_limit: s.user_limit!,
    balance: s.balance!,
    trial_ends_at: s.trial_ends_at ?? null,
    current_period_ends_at: s.current_period_ends_at ?? null,
    canceled_at: s.canceled_at ?? null,
    created_at: s.created_at!,
    updated_at: s.updated_at!,
    last_payment_attempt_at: s.last_payment_attempt_at ?? null,
    payment_failures: s.payment_failures ?? null,
    grace_period_ends_at: s.grace_period_ends_at ?? null,
  };
}

function toPlanRow(p: z.infer<typeof subscriptionPlanSchema>): SubscriptionPlanRow {
  return {
    id: p.id!,
    name: p.name!,
    name_en: p.name_en ?? null,
    description: p.description ?? null,
    description_en: p.description_en ?? null,
    trial_days: p.trial_days!,
    annual_discount_percent: p.annual_discount_percent!,
    is_active: p.is_active!,
    created_at: p.created_at!,
    updated_at: p.updated_at!,
  };
}

function toTierRow(t: z.infer<typeof subscriptionPricingTierSchema>): PricingTierRow {
  return {
    id: t.id!,
    plan_id: t.plan_id!,
    tier_type: t.tier_type!,
    min_count: t.min_count!,
    max_count: t.max_count ?? null,
    price_per_unit: t.price_per_unit!,
    created_at: t.created_at!,
  };
}

function toSettingRow(s: z.infer<typeof pricingSettingSchema>): PricingSettingRow {
  return {
    id: s.id!,
    key: s.key!,
    value: (s.value ?? null) as PricingSettingRow["value"],
    description: s.description ?? null,
    updated_by: s.updated_by ?? null,
    updated_at: s.updated_at!,
    created_at: s.created_at!,
  };
}

function toSubscriptionWithCompanyRow(
  s: z.infer<typeof subscriptionWithCompanySchema>,
): SubscriptionWithCompanyRow {
  return {
    ...toSubscriptionRow(s),
    company: s.company
      ? {
          id: s.company.id!,
          name: s.company.name!,
          name_en: s.company.name_en ?? null,
          email: s.company.email ?? null,
          phone: s.company.phone ?? null,
        }
      : null,
  };
}

function toAdminStats(s: z.infer<typeof subscriptionAdminStatsSchema>): SubscriptionAdminStats {
  return {
    total_excluding_canceled: s.total_excluding_canceled!,
    active: s.active!,
    trial: s.trial!,
    new_this_month_excluding_canceled: s.new_this_month_excluding_canceled!,
    active_balance_sum: s.active_balance_sum!,
  };
}

/**
 * Write-path fallback guard (owners.create precedent): a deterministic
 * non-auth 4xx (subs-002 tier-not-found, subs-003 plan-not-found, Zod 400) is
 * Nest's REAL answer — rethrow it so the UI surfaces the server's bilingual
 * message instead of re-running the write through Supabase. 401/403 still fall
 * back (the RLS path yields today's exact net behavior — e.g. a non-SA write
 * is a silent no-op); everything ambiguous defers to
 * `reportNestFallbackOrRethrowUnknownCommit` (duplicate-write guard).
 */
function reportWriteFallbackOrRethrow(scope: string, err: unknown): void {
  if (
    err instanceof NestRequestError &&
    err.kind === "http" &&
    err.status !== undefined &&
    err.status >= 400 &&
    err.status < 500 &&
    err.status !== 401 &&
    err.status !== 403
  ) {
    throw err;
  }
  reportNestFallbackOrRethrowUnknownCommit(scope, err);
}

// ─── Supabase implementations (armed fallback bodies) ─────────────────────────

async function getByCompanyViaSupabase(companyId: string): Promise<SubscriptionIdRow | null> {
  const { data, error } = await supabase
    .from("subscriptions")
    .select("id")
    .eq("company_id", companyId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching company subscription:", serializeError(error));
    throw error;
  }

  return data;
}

async function getFullByCompanyViaSupabase(companyId: string): Promise<SubscriptionRow | null> {
  const { data, error } = await supabase
    .from("subscriptions")
    .select("*")
    .eq("company_id", companyId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching company subscription:", serializeError(error));
    throw error;
  }

  return data;
}

async function updatePlanSelectionViaSupabase(
  id: string,
  companyId: string,
  patch: Pick<TablesUpdate<"subscriptions">, "unit_limit" | "user_limit" | "billing_cycle" | "status">,
): Promise<void> {
  const { error } = await supabase
    .from("subscriptions")
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq("id", id)
    .eq("company_id", companyId);

  if (error) {
    console.error("Error updating subscription plan:", serializeError(error));
    throw error;
  }
}

async function listActivePlansViaSupabase(): Promise<SubscriptionPlanRow[]> {
  const { data, error } = await supabase
    .from("subscription_plans")
    .select("*")
    .eq("is_active", true)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Error fetching subscription plans:", serializeError(error));
    throw error;
  }

  return data ?? [];
}

async function getActivePlanIdViaSupabase(): Promise<string | null> {
  const { data, error } = await supabase
    .from("subscription_plans")
    .select("id")
    .eq("is_active", true)
    .limit(1)
    .maybeSingle();

  if (error) {
    console.error("Error fetching active plan id:", serializeError(error));
    throw error;
  }

  return data?.id ?? null;
}

async function listPricingTiersViaSupabase(planId?: string): Promise<PricingTierRow[]> {
  let query = supabase
    .from("subscription_pricing_tiers")
    .select("*")
    .order("min_count", { ascending: true });

  if (planId) {
    query = query.eq("plan_id", planId);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching pricing tiers:", serializeError(error));
    throw error;
  }

  return data ?? [];
}

async function listPricingTiersSortedViaSupabase(): Promise<PricingTierRow[]> {
  const { data, error } = await supabase
    .from("subscription_pricing_tiers")
    .select("*")
    .order("tier_type")
    .order("min_count", { ascending: true });

  if (error) {
    console.error("Error fetching pricing tiers:", serializeError(error));
    throw error;
  }

  return data ?? [];
}

async function updatePricingTierViaSupabase(
  id: string,
  patch: Pick<TablesUpdate<"subscription_pricing_tiers">, "min_count" | "max_count" | "price_per_unit">,
): Promise<void> {
  const { error } = await supabase
    .from("subscription_pricing_tiers")
    .update(patch)
    .eq("id", id);

  if (error) {
    console.error("Error updating pricing tier:", serializeError(error));
    throw error;
  }
}

async function insertPricingTierViaSupabase(
  tier: Pick<
    TablesInsert<"subscription_pricing_tiers">,
    "plan_id" | "tier_type" | "min_count" | "max_count" | "price_per_unit"
  >,
): Promise<void> {
  const { error } = await supabase.from("subscription_pricing_tiers").insert(tier);

  if (error) {
    console.error("Error inserting pricing tier:", serializeError(error));
    throw error;
  }
}

async function deletePricingTierViaSupabase(id: string): Promise<void> {
  const { error } = await supabase.from("subscription_pricing_tiers").delete().eq("id", id);

  if (error) {
    console.error("Error deleting pricing tier:", serializeError(error));
    throw error;
  }
}

async function listPricingSettingsViaSupabase(): Promise<PricingSettingRow[]> {
  const { data, error } = await supabase
    .from("pricing_settings")
    .select("*")
    .order("key", { ascending: true });

  if (error) {
    console.error("Error fetching pricing settings:", serializeError(error));
    throw error;
  }

  return data ?? [];
}

async function getPricingSettingValueViaSupabase(
  key: string,
): Promise<PricingSettingRow["value"] | null> {
  const { data, error } = await supabase
    .from("pricing_settings")
    .select("value")
    .eq("key", key)
    .maybeSingle();

  if (error) {
    console.error("Error fetching pricing setting:", serializeError(error));
    return null;
  }

  return data?.value ?? null;
}

async function upsertPricingSettingViaSupabase(
  setting: Pick<TablesInsert<"pricing_settings">, "key" | "value" | "updated_by">,
): Promise<void> {
  const { error } = await supabase
    .from("pricing_settings")
    .upsert({ ...setting, updated_at: new Date().toISOString() }, { onConflict: "key" });

  if (error) {
    console.error("Error upserting pricing setting:", serializeError(error));
    throw error;
  }
}

async function listAllWithCompanyForSuperAdminViaSupabase(): Promise<SubscriptionWithCompanyRow[]> {
  const { data, error } = await supabase
    .from("subscriptions")
    .select(
      `
        *,
        company:companies (
          id,
          name,
          name_en,
          email,
          phone
        )
      `,
    )
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching subscribers:", serializeError(error));
    throw error;
  }

  return (data ?? []) as unknown as SubscriptionWithCompanyRow[];
}

/**
 * Fallback for `adminStatsForSuperAdmin` — composes the same 5 legacy queries
 * the super-admin Dashboard issued before the a13 consolidation.
 */
async function adminStatsForSuperAdminViaSupabase(): Promise<SubscriptionAdminStats> {
  const startOfMonth = new Date();
  startOfMonth.setDate(1);
  startOfMonth.setHours(0, 0, 0, 0);

  const countByStatus = async (status: SubscriptionStatus): Promise<number> => {
    const { count, error } = await supabase
      .from("subscriptions")
      .select("*", { count: "exact", head: true })
      .eq("status", status);
    if (error) {
      console.error("Error counting subscriptions by status:", serializeError(error));
      throw error;
    }
    return count ?? 0;
  };

  const countExcludingCanceled = async (sinceIso?: string): Promise<number> => {
    let query = supabase
      .from("subscriptions")
      .select("*", { count: "exact", head: true })
      .neq("status", "canceled");
    if (sinceIso) {
      query = query.gte("created_at", sinceIso);
    }
    const { count, error } = await query;
    if (error) {
      console.error("Error counting subscriptions:", serializeError(error));
      throw error;
    }
    return count ?? 0;
  };

  const listActiveBalances = async (): Promise<Pick<SubscriptionRow, "balance">[]> => {
    const { data, error } = await supabase
      .from("subscriptions")
      .select("balance")
      .eq("status", "active");
    if (error) {
      console.error("Error fetching active subscription balances:", serializeError(error));
      throw error;
    }
    return data ?? [];
  };

  const [total, active, trial, newThisMonth, balances] = await Promise.all([
    countExcludingCanceled(),
    countByStatus("active"),
    countByStatus("trial"),
    countExcludingCanceled(startOfMonth.toISOString()),
    listActiveBalances(),
  ]);

  return {
    total_excluding_canceled: total,
    active,
    trial,
    new_this_month_excluding_canceled: newThisMonth,
    active_balance_sum: balances.reduce((sum, s) => sum + (s.balance || 0), 0),
  };
}

export const subscriptionsRepository = {
  // ─── Company-scoped subscription ──────────────────────────────

  /**
   * The company's subscription row (id only), or null when none exists.
   * Nest-first via GET /subscriptions/me (200-with-null when absent — the
   * endpoint never app-404s, so no definitive-404 handling is needed).
   */
  async getByCompany(companyId: string): Promise<SubscriptionIdRow | null> {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.subscriptions.me());
        const parsed = subscriptionSchema.nullable().parse(row ?? null);
        return parsed ? { id: parsed.id! } : null;
      } catch (err) {
        reportNestFallback("subscriptions.getByCompany", err);
      }
    }
    return getByCompanyViaSupabase(companyId);
  },

  /** The company's full subscription row, or null when none exists. Nest-first (GET /subscriptions/me). */
  async getFullByCompany(companyId: string): Promise<SubscriptionRow | null> {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.subscriptions.me());
        const parsed = subscriptionSchema.nullable().parse(row ?? null);
        return parsed ? toSubscriptionRow(parsed) : null;
      } catch (err) {
        reportNestFallback("subscriptions.getFullByCompany", err);
      }
    }
    return getFullByCompanyViaSupabase(companyId);
  },

  /**
   * Update a company's own subscription (plan selection).
   *
   * Nest path (PUT /subscriptions/me/plan): sends the patch ONLY — the server
   * derives the row from the JWT's company (one sub per company), so the
   * `id`/`companyId` arguments are not transmitted and a stale/foreign id can
   * never cross a tenant boundary (stronger than the fallback's double-filter).
   * Supabase fallback: keeps the id+company_id defence-in-depth filter.
   */
  async updatePlanSelection(
    id: string,
    companyId: string,
    patch: Pick<TablesUpdate<"subscriptions">, "unit_limit" | "user_limit" | "billing_cycle" | "status">,
  ): Promise<void> {
    if (API_URL) {
      try {
        await nestRequest<unknown>(API_ROUTES.subscriptions.updateMyPlan(), {
          method: "PUT",
          body: JSON.stringify(patch),
        });
        return;
      } catch (err) {
        reportWriteFallbackOrRethrow("subscriptions.updatePlanSelection", err);
      }
    }
    return updatePlanSelectionViaSupabase(id, companyId, patch);
  },

  // ─── Plan catalog (config reads) ──────────────────────────────

  /** All active subscription plans, oldest first. Nest-first (GET /subscriptions/plans). */
  async listActivePlans(): Promise<SubscriptionPlanRow[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.subscriptions.plans());
        return z.array(subscriptionPlanSchema).parse(rows).map(toPlanRow);
      } catch (err) {
        reportNestFallback("subscriptions.listActivePlans", err);
      }
    }
    return listActivePlansViaSupabase();
  },

  /**
   * The first active plan's id (used when creating a pricing tier).
   * Nest path: first of the created_at-asc active plans — deterministic, where
   * the legacy Supabase `limit(1)` without ORDER returned an arbitrary row
   * (equivalent in practice: a single active plan exists).
   */
  async getActivePlanId(): Promise<string | null> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.subscriptions.plans());
        return z.array(subscriptionPlanSchema).parse(rows)[0]?.id ?? null;
      } catch (err) {
        reportNestFallback("subscriptions.getActivePlanId", err);
      }
    }
    return getActivePlanIdViaSupabase();
  },

  // ─── Pricing tiers ────────────────────────────────────────────

  /**
   * Pricing tiers, optionally for one plan. Nest-first
   * (GET /subscriptions/pricing-tiers?planId=). Server orders
   * `tier_type asc, min_count asc` (legacy planId path ordered `min_count`
   * only — unobservable: both callers split by tier_type before consuming
   * order); the fallback keeps the legacy ordering.
   */
  async listPricingTiers(planId?: string): Promise<PricingTierRow[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.subscriptions.pricingTiers(planId));
        return z.array(subscriptionPricingTierSchema).parse(rows).map(toTierRow);
      } catch (err) {
        reportNestFallback("subscriptions.listPricingTiers", err);
      }
    }
    return listPricingTiersViaSupabase(planId);
  },

  /** All pricing tiers, ordered by tier_type then min_count (super-admin config table). */
  async listPricingTiersSorted(): Promise<PricingTierRow[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.subscriptions.pricingTiers());
        return z.array(subscriptionPricingTierSchema).parse(rows).map(toTierRow);
      } catch (err) {
        reportNestFallback("subscriptions.listPricingTiersSorted", err);
      }
    }
    return listPricingTiersSortedViaSupabase();
  },

  /** Update a pricing tier's bounds/price (super_admin config). Nest-first (PUT). */
  async updatePricingTier(
    id: string,
    patch: Pick<TablesUpdate<"subscription_pricing_tiers">, "min_count" | "max_count" | "price_per_unit">,
  ): Promise<void> {
    if (API_URL) {
      try {
        await nestRequest<unknown>(API_ROUTES.subscriptions.pricingTier(id), {
          method: "PUT",
          body: JSON.stringify(patch),
        });
        return;
      } catch (err) {
        reportWriteFallbackOrRethrow("subscriptions.updatePricingTier", err);
      }
    }
    return updatePricingTierViaSupabase(id, patch);
  },

  /** Insert a new pricing tier (super_admin config). Nest-first (POST). */
  async insertPricingTier(
    tier: Pick<
      TablesInsert<"subscription_pricing_tiers">,
      "plan_id" | "tier_type" | "min_count" | "max_count" | "price_per_unit"
    >,
  ): Promise<void> {
    if (API_URL) {
      try {
        await nestRequest<unknown>(API_ROUTES.subscriptions.createPricingTier(), {
          method: "POST",
          body: JSON.stringify(tier),
        });
        return;
      } catch (err) {
        reportWriteFallbackOrRethrow("subscriptions.insertPricingTier", err);
      }
    }
    return insertPricingTierViaSupabase(tier);
  },

  /** Delete a pricing tier (super_admin config). Nest-first (DELETE — 204). */
  async deletePricingTier(id: string): Promise<void> {
    if (API_URL) {
      try {
        await nestRequest<unknown>(API_ROUTES.subscriptions.pricingTier(id), {
          method: "DELETE",
        });
        return;
      } catch (err) {
        reportWriteFallbackOrRethrow("subscriptions.deletePricingTier", err);
      }
    }
    return deletePricingTierViaSupabase(id);
  },

  // ─── Pricing settings ─────────────────────────────────────────

  /**
   * All pricing settings, ordered by key. Nest-first (SA-gated): a non-SA
   * caller gets a Nest 403 → armed fallback → RLS-filtered `[]` → identical
   * render to today. The fallback's original justification (PricingConfigurator
   * inside DemoExitModal) went away with the sales-led demo CTA in #3531 — it
   * now has no non-SA caller, so whether to keep it is under review.
   */
  async listPricingSettings(): Promise<PricingSettingRow[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.subscriptions.pricingSettings());
        return z.array(pricingSettingSchema).parse(rows).map(toSettingRow);
      } catch (err) {
        reportNestFallback("subscriptions.listPricingSettings", err);
      }
    }
    return listPricingSettingsViaSupabase();
  },

  /**
   * The raw `value` of a single pricing setting by key, or null when the row
   * is missing/unreadable. Callers own the shape of `value` (stored as JSON).
   *
   * Nest-backed ONLY for the allowlisted `service_catalog` key
   * (GET /subscriptions/pricing-settings/service-catalog — auth-only because
   * the owner-portal "Request Service" catalog reads it; the server hardcodes
   * the key, so there is no arbitrary-key config read for non-SA). Any other
   * key goes straight to the Supabase path. Deliberate intent-fix: RLS
   * silently nulled this read for non-super-admins, breaking the hook's
   * documented "finance can change a fee without a deploy" purpose (a13 §5 D1).
   */
  async getPricingSettingValue(key: string): Promise<PricingSettingRow["value"] | null> {
    if (API_URL && key === "service_catalog") {
      try {
        const res = await nestRequest<unknown>(API_ROUTES.subscriptions.serviceCatalogSetting());
        const parsed = serviceCatalogValueSchema.parse(res);
        return (parsed.value ?? null) as PricingSettingRow["value"] | null;
      } catch (err) {
        reportNestFallback("subscriptions.getPricingSettingValue", err);
      }
    }
    return getPricingSettingValueViaSupabase(key);
  },

  /**
   * Upsert a single pricing setting by key (super_admin config). Nest-first
   * (PUT /subscriptions/pricing-settings/:key) — the Nest body drops
   * `updated_by` (the server stamps it from the JWT; the client can no longer
   * spoof it). The Supabase fallback keeps sending it (legacy behavior).
   */
  async upsertPricingSetting(
    setting: Pick<TablesInsert<"pricing_settings">, "key" | "value" | "updated_by">,
  ): Promise<void> {
    if (API_URL) {
      try {
        await nestRequest<unknown>(API_ROUTES.subscriptions.pricingSetting(setting.key), {
          method: "PUT",
          body: JSON.stringify({ value: setting.value }),
        });
        return;
      } catch (err) {
        reportWriteFallbackOrRethrow("subscriptions.upsertPricingSetting", err);
      }
    }
    return upsertPricingSettingViaSupabase(setting);
  },

  // ─── Super-admin cross-tenant reads (NOT company-scoped) ──────

  /**
   * ALL companies' subscriptions with their embedded company, newest first.
   * Cross-tenant by design — SuperAdminGuard on the Nest side; never add a
   * per-company filter here.
   */
  async listAllWithCompanyForSuperAdmin(): Promise<SubscriptionWithCompanyRow[]> {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.subscriptions.adminList());
        return z
          .array(subscriptionWithCompanySchema)
          .parse(rows)
          .map(toSubscriptionWithCompanyRow);
      } catch (err) {
        reportNestFallback("subscriptions.listAllWithCompanyForSuperAdmin", err);
      }
    }
    return listAllWithCompanyForSuperAdminViaSupabase();
  },

  /**
   * The super-admin dashboard's subscription stats in ONE server aggregate
   * (GET /subscriptions/admin/stats) — replaces the 5 discrete round-trips
   * (total/active/trial counts, new-this-month, active balances for MRR).
   * Cross-tenant by design. Fallback composes the same 5 legacy queries.
   */
  async adminStatsForSuperAdmin(): Promise<SubscriptionAdminStats> {
    if (API_URL) {
      try {
        const res = await nestRequest<unknown>(API_ROUTES.subscriptions.adminStats());
        return toAdminStats(subscriptionAdminStatsSchema.parse(res));
      } catch (err) {
        reportNestFallback("subscriptions.adminStatsForSuperAdmin", err);
      }
    }
    return adminStatsForSuperAdminViaSupabase();
  },
};
